/**
 * Provider FAQ (KidHive). Plain-language help for providers, rendered by FaqPage with the same
 * LegalContent block renderer the legal pages use. Client-authored copy; edit the text here.
 * A closing `note` points to the governing legal documents.
 */
const content = [
  { type: 'h2', text: "What is KidHive, and how does it help a provider's business?" },
  {
    type: 'p',
    text:
      "KidHive fills empty seats. Parents are already searching for kids' activities across Instagram, Yelp, and mom groups — KidHive puts a provider's classes in front of them in one place, with real-time booking and payment built in. It costs nothing to join, and providers only pay when a booking actually happens, so there's no risk in listing spare capacity alongside whatever system a provider already uses.",
  },

  { type: 'h2', text: 'Is it free to list on KidHive?' },
  {
    type: 'p',
    text:
      'Yes. There are no membership or listing fees for providers. KidHive only earns money through a platform fee when a booking actually happens.',
  },

  { type: 'h2', text: 'How much is the platform fee, and how do providers get paid?' },
  {
    type: 'p',
    text:
      "The standard platform fee is 15%. Founding Members — the first 50 providers to join KidHive in the Los Angeles / Orange County launch area — pay 12%, locked for life. The platform fee and Stripe processing fee appear on every class receipt. Payments run through Stripe Connect straight to the provider's account. Questions about fees? Email info@KidHive.co.",
  },

  { type: 'h2', text: 'How does money get from the parent to the provider?' },
  {
    type: 'p',
    text:
      "The parent pays the full price at booking, through Stripe, inside KidHive. The charge settles to the provider's connected Stripe account with KidHive's platform fee and Stripe's processing fee deducted automatically — nothing to invoice or collect. The remaining balance sits in the provider's Stripe account until the payout releases, and Stripe then deposits it to the provider's bank account. KidHive never holds the provider's money.",
  },

  { type: 'h2', text: 'Do providers need a Stripe account?' },
  {
    type: 'p',
    text:
      "Yes — a connected Stripe account is required to host classes and operate on KidHive; providers can't publish listings or receive bookings without one. Providers should also keep their Stripe information complete and up to date, both so payouts aren't interrupted and so Stripe can issue accurate end-of-year tax documents (such as a 1099 form, where applicable).",
  },

  { type: 'h2', text: 'When do providers get paid?' },
  {
    type: 'p',
    text:
      "For a single drop-in session, the payout releases 7 days after the session has been completed. For camps and multi-week classes, the payout releases on the last day of the program. A provider's first payout may take a little longer while Stripe completes its security and verification checks. After a payout releases, Stripe transfers it to the provider's bank account on its standard schedule.",
  },

  { type: 'h2', text: 'What is the Founding Member program?' },
  {
    type: 'p',
    text:
      "The first 50 providers to join KidHive in Los Angeles / Orange County become Founding Members: a 12% platform fee locked for the life of the account (versus the 15% standard rate), plus a “Founding Member” badge on the provider's profile and listings. Locations added during a Founding Member's initial setup share the 12% rate; locations added after the founding list closes are at the standard 15%. If KidHive ever changes the standard platform fee, providers receive at least 30 days' notice before a new percentage takes effect — a Founding Member's locked rate never goes up.",
  },

  { type: 'h2', text: 'Can KidHive be used alongside an existing booking system?' },
  {
    type: 'p',
    text:
      "Yes — KidHive is built to coexist with a provider's current booking system. Since KidHive doesn't sync with other software, the provider is responsible for making sure classes never get oversold across both — the easiest way is to dedicate a set number of spots to KidHive and reduce the other system's capacity by that same amount — and for notifying families of any changes made outside KidHive. Some smaller businesses skip that balancing act entirely by using KidHive as their only booking system, especially if they're looking to avoid another platform's fees.",
  },

  { type: 'h2', text: 'Do providers set their own cancellation policy?' },
  {
    type: 'p',
    text:
      'No — KidHive uses one platform-wide policy for every booking, applied and enforced automatically.',
  },

  { type: 'h2', text: 'How far ahead can providers schedule bookable classes?' },
  {
    type: 'p',
    text:
      "Providers can schedule classes as far out as they like — including full terms and camps — but parents can only book and pay once a class (or a program's first session) is within 6 months. Dates further out become bookable as they come into the window.",
  },

  { type: 'h2', text: 'Can a provider cancel or reschedule a published class?' },
  {
    type: 'p',
    text:
      'A provider can cancel or reschedule a single drop-in session from the dashboard when needed. On a reschedule, the parent chooses: accept the new date/time (no money moves — the booking simply transfers) or decline (the parent is fully refunded). Camps and multi-week classes can’t be rescheduled at this time — to move one to a new date/time, the provider cancels it and republishes it as a new listing; to cancel one, simply cancel it from the dashboard. Canceling or rescheduling classes often may result in suspension of the provider account.',
  },

  {
    type: 'h2',
    text: 'Can a provider cancel or reschedule individual days within a camp or multi-week class?',
  },
  {
    type: 'p',
    text:
      "Not at this time — canceling or rescheduling individual days within a camp or multi-week class isn't currently supported, for either side. A provider who can't run a particular day can exchange messages with the affected parents on KidHive and agree on a make-up session — the provider is expected to offer affected parents a make-up session where possible. If the provider cancels the entire camp or multi-week class, every parent with an active booking is refunded in full, and the fee handling described below applies. KidHive understands this can be inconvenient in the platform's current state, and will introduce more flexible capabilities as KidHive grows and improves over time.",
  },

  { type: 'h2', text: "What happens to a provider's payout if a booking is canceled or refunded?" },
  {
    type: 'p',
    text:
      "If a parent cancels per the refund rules, the provider keeps the corresponding share and KidHive's fee is reduced proportionally. If the provider cancels a booking — or a parent declines a proposed reschedule — the parent gets a full refund, KidHive's fee is fully reversed, and the provider is responsible for the Stripe processing fee on the refunded booking. The same applies if KidHive cancels a class — whether for a suspension, on behalf of the provider, or a force-majeure event. In short: the Stripe processing fee on a refunded booking is always the provider's responsibility, no matter who cancels.",
  },

  { type: 'h2', text: 'What are providers responsible for?' },
  {
    type: 'p',
    text:
      'Providers are responsible for their licenses, permits, insurance, and background checks, and for complying with child-safety and other applicable laws. Providers are also responsible for delivering activities as described.',
  },

  { type: 'h2', text: "How does a provider learn about a child's allergies?" },
  {
    type: 'p',
    text:
      "Any allergies a parent records are passed to the provider with the booking. That's a starting point, not a guarantee — the provider is responsible for asking each family and confirming any allergies when the child arrives. KidHive forwards this information as a convenience but isn't responsible for acting on it.",
  },

  { type: 'h2', text: 'Can a provider have parents sign its own waiver or forms?' },
  {
    type: 'p',
    text:
      "Yes — if the activity requires a waiver, release, or other document, the provider handles that directly with families when they arrive, and is responsible for presenting, collecting, and retaining it. KidHive doesn't provide, collect, or store these documents.",
  },

  { type: 'h2', text: 'What if a provider and a parent disagree about a class?' },
  {
    type: 'p',
    text:
      "The class runs under a contract between the provider and the parent, so any disagreement — before, during, or after — is theirs to resolve. For a payment or booking problem, KidHive will help in good faith and provide the booking records it has, but it doesn't arbitrate disputes, take sides, or accept responsibility for the activity itself or its outcome. Providers and parents are encouraged to resolve issues directly.",
  },

  {
    type: 'h2',
    text: 'Can a provider take a family met through KidHive off-platform for future bookings?',
  },
  {
    type: 'p',
    text:
      "No — if a parent found the provider through KidHive, the provider can't redirect them to its own site or another platform to avoid the fee. That's a material breach of the Provider Agreement and can lead to suspension. Parents who find a provider independently aren't affected.",
  },

  { type: 'h2', text: 'How do providers get approved and listed, and can they opt out later?' },
  {
    type: 'p',
    text:
      'Sign up, build a profile, and connect a Stripe account for payouts — the KidHive team reviews new providers and listings before they go live. To opt out or remove a listing, email info@KidHive.co; requests are processed within 10 business days.',
  },

  {
    type: 'note',
    text:
      "This FAQ is a plain-language summary — it doesn't replace KidHive's Terms of Service, Provider Agreement, Cancellation & Refund Policy, or Privacy Policy, which govern if there's ever a difference. Questions about anything here? Email info@KidHive.co.",
  },
];

export default content;
